import { useMemo, useState } from 'react';

/**
 * Client-side search + sort + pagination for the DataTable.
 * Ported from D:\campaigning-system\src\hooks\useTableControls.ts.
 */
export function useTableControls({ data, pageSize = 8, searchKeys = [], initialSort = { key: null, dir: 'asc' } }) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(initialSort);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || searchKeys.length === 0) return data;
    return data.filter((row) => searchKeys.some((k) => String(row[k] ?? '').toLowerCase().includes(q)));
  }, [data, search, searchKeys]);

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const key = sort.key;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor;
      return String(av).localeCompare(String(bv)) * factor;
    });
  }, [filtered, sort]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const paged = useMemo(() => sorted.slice((safePage - 1) * pageSize, safePage * pageSize), [sorted, safePage, pageSize]);

  function toggleSort(key) {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
    setPage(1);
  }
  function updateSearch(v) {
    setSearch(v);
    setPage(1);
  }

  return { search, setSearch: updateSearch, sort, toggleSort, page: safePage, setPage, pageSize, total: sorted.length, rows: paged };
}

export default useTableControls;
